import { useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Copy, RefreshCw, Trash2 } from "lucide-react";
import { Toast } from "../../components/Toast";

type Tone = "modern" | "playful" | "premium" | "minimal" | "classic";
type Format = "one-word" | "two-word" | "hyphenated" | "suffix" | "invented";

type IndustryLexicon = {
  nouns: string[];
  suffixes: string[];
  prefixes: string[];
};

const TONE_ADJECTIVES: Record<Tone, string[]> = {
  modern: ["Nova", "Flux", "Pulse", "Vector", "Orbit", "Nex", "Spark", "Signal"],
  playful: ["Sunny", "Bouncy", "Happy", "Zippy", "Witty", "Chirp", "Jolly", "Merry"],
  premium: ["Aurum", "Regal", "Luxe", "Noble", "Gilded", "Crown", "Prime", "Opal"],
  minimal: ["Pure", "Clear", "Mono", "Quiet", "Simple", "Plain", "Clean", "Soft"],
  classic: ["Heritage", "Union", "Crest", "Tradition", "Standard", "Legacy", "Anchor", "Summit"],
};

const INDUSTRY_LEXICON: Record<string, IndustryLexicon> = {
  general: {
    nouns: ["Studio", "Works", "Collective", "Foundry", "Craft", "Supply", "Union", "House"],
    suffixes: ["Co", "Company", "Group", "Partners", "& Co", "Collective"],
    prefixes: ["North", "Blue", "Ever", "Bright", "True", "Summit"],
  },
  tech: {
    nouns: ["Labs", "Systems", "Cloud", "Stack", "Circuit", "Data", "Signal", "Logic"],
    suffixes: ["Labs", "Systems", "Tech", "AI", "Works", "Studio"],
    prefixes: ["Neo", "Hyper", "Quantum", "Digital", "Meta", "Pixel"],
  },
  finance: {
    nouns: ["Capital", "Wealth", "Equity", "Ledger", "Trust", "Funds", "Holdings", "Advisors"],
    suffixes: ["Capital", "Partners", "Advisors", "Holdings", "Group"],
    prefixes: ["Crest", "Prime", "Legacy", "Summit", "Anchor", "Keystone"],
  },
  health: {
    nouns: ["Wellness", "Care", "Clinic", "Vital", "Remedy", "Balance", "Therapy", "Healing"],
    suffixes: ["Health", "Care", "Clinic", "Wellness", "Center"],
    prefixes: ["True", "Pure", "Vital", "Bright", "Ever", "Harmony"],
  },
  food: {
    nouns: ["Kitchen", "Roastery", "Bakery", "Table", "Pantry", "Harvest", "Bistro", "Spice"],
    suffixes: ["Kitchen", "Roastery", "Bakery", "Market", "Table"],
    prefixes: ["Golden", "Fresh", "Sunny", "Daily", "River", "Stone"],
  },
  fashion: {
    nouns: ["Atelier", "Threads", "Stitch", "Wardrobe", "Couture", "Boutique", "Loom", "Fabric"],
    suffixes: ["Atelier", "Couture", "Studio", "Boutique", "Threads"],
    prefixes: ["Velvet", "Silk", "Opal", "Luna", "Muse", "Ever"],
  },
  realEstate: {
    nouns: ["Realty", "Estates", "Homes", "Properties", "Land", "Keys", "Foundations"],
    suffixes: ["Realty", "Estates", "Homes", "Properties", "Group"],
    prefixes: ["Stone", "Crest", "Summit", "Harbor", "Anchor", "Blue"],
  },
  marketing: {
    nouns: ["Media", "Growth", "Reach", "Brand", "Spark", "Campaign", "Story", "Signal"],
    suffixes: ["Media", "Marketing", "Studio", "Agency", "Growth"],
    prefixes: ["Bright", "Bold", "True", "Signal", "High", "North"],
  },
  education: {
    nouns: ["Academy", "Learning", "School", "Mentor", "Study", "Labs", "Classroom"],
    suffixes: ["Academy", "Learning", "Institute", "School", "Labs"],
    prefixes: ["Bright", "Open", "Curious", "Summit", "Beacon"],
  },
  travel: {
    nouns: ["Journey", "Voyage", "Trail", "Wander", "Passport", "Atlas", "Roam"],
    suffixes: ["Travel", "Journeys", "Adventures", "Tours", "Getaways"],
    prefixes: ["Far", "Open", "Wild", "Blue", "Golden", "Atlas"],
  },
  ecommerce: {
    nouns: ["Market", "Cart", "Store", "Commerce", "Supply", "Ship", "Parcel"],
    suffixes: ["Commerce", "Market", "Supply", "Store", "Cart"],
    prefixes: ["Swift", "Bright", "Prime", "Rapid", "True", "Ever"],
  },
  fitness: {
    nouns: ["Strength", "Motion", "Pulse", "Fit", "Core", "Active", "Training"],
    suffixes: ["Fitness", "Training", "Studio", "Gym", "Performance"],
    prefixes: ["Peak", "Strong", "Power", "Bold", "Core", "True"],
  },
  services: {
    nouns: ["Solutions", "Support", "Care", "Service", "Partners", "Group", "Assist"],
    suffixes: ["Solutions", "Services", "Partners", "Group", "Support"],
    prefixes: ["Trusted", "Bright", "Ever", "Prime", "Unity", "Clear"],
  },
};

const SYLLABLES = [
  "lo", "vi", "ra", "zen", "tri", "qa", "ly", "on", "va", "ce", "li", "mo",
  "no", "ka", "sa", "ta", "vi", "na", "re", "su", "fi", "xo", "tu", "ver",
  "zen", "pol", "jar", "rhy", "flux", "mar", "sol", "vera", "nix", "ora",
];

const getRandomInt = (max: number): number => {
  if (max <= 0) return 0;
  if (typeof crypto !== "undefined" && "getRandomValues" in crypto) {
    const buffer = new Uint32Array(1);
    crypto.getRandomValues(buffer);
    return buffer[0] % max;
  }
  return Math.floor(Math.random() * max);
};

const pick = <T,>(list: T[]): T => list[getRandomInt(list.length)];

const normalizeKeyword = (value: string) =>
  value
    .trim()
    .replace(/[^a-z0-9\s&-]/gi, "")
    .replace(/\s+/g, " ")
    .trim();

const toTitleCase = (value: string) =>
  value
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const buildInventedName = (keyword?: string) => {
  const syllableCount = getRandomInt(2) + 2;
  const pieces: string[] = [];

  if (keyword && keyword.length >= 3) {
    pieces.push(keyword.slice(0, 3).toLowerCase());
  }

  while (pieces.join("").length < syllableCount * 2) {
    pieces.push(pick(SYLLABLES));
  }

  const raw = pieces.join("");
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

export function BusinessNameGenerator() {
  const { t } = useTranslation();
  const [keywordsInput, setKeywordsInput] = useState("");
  const [industry, setIndustry] = useState("general");
  const [tone, setTone] = useState<Tone>("modern");
  const [format, setFormat] = useState<Format>("two-word");
  const [count, setCount] = useState(12);
  const [includeKeyword, setIncludeKeyword] = useState(true);
  const [preferAlliteration, setPreferAlliteration] = useState(false);
  const [useSuffix, setUseSuffix] = useState(true);
  const [names, setNames] = useState<string[]>([]);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const keywords = useMemo(() => {
    if (!keywordsInput.trim()) return [];
    const parts = keywordsInput
      .split(/[\n,]/)
      .map((part) => normalizeKeyword(part))
      .filter(Boolean);
    return Array.from(new Set(parts.map((part) => toTitleCase(part))));
  }, [keywordsInput]);

  const industryOptions = useMemo(
    () => [
      { value: "general", label: t("businessNameGenerator.industry.general") },
      { value: "tech", label: t("businessNameGenerator.industry.tech") },
      { value: "finance", label: t("businessNameGenerator.industry.finance") },
      { value: "health", label: t("businessNameGenerator.industry.health") },
      { value: "food", label: t("businessNameGenerator.industry.food") },
      { value: "fashion", label: t("businessNameGenerator.industry.fashion") },
      { value: "realEstate", label: t("businessNameGenerator.industry.realEstate") },
      { value: "marketing", label: t("businessNameGenerator.industry.marketing") },
      { value: "education", label: t("businessNameGenerator.industry.education") },
      { value: "travel", label: t("businessNameGenerator.industry.travel") },
      { value: "ecommerce", label: t("businessNameGenerator.industry.ecommerce") },
      { value: "fitness", label: t("businessNameGenerator.industry.fitness") },
      { value: "services", label: t("businessNameGenerator.industry.services") },
    ],
    [t]
  );

  const toneOptions: { value: Tone; label: string }[] = useMemo(
    () => [
      { value: "modern", label: t("businessNameGenerator.tone.modern") },
      { value: "playful", label: t("businessNameGenerator.tone.playful") },
      { value: "premium", label: t("businessNameGenerator.tone.premium") },
      { value: "minimal", label: t("businessNameGenerator.tone.minimal") },
      { value: "classic", label: t("businessNameGenerator.tone.classic") },
    ],
    [t]
  );

  const formatOptions: { value: Format; label: string }[] = useMemo(
    () => [
      { value: "one-word", label: t("businessNameGenerator.format.oneWord") },
      { value: "two-word", label: t("businessNameGenerator.format.twoWord") },
      { value: "hyphenated", label: t("businessNameGenerator.format.hyphenated") },
      { value: "suffix", label: t("businessNameGenerator.format.suffix") },
      { value: "invented", label: t("businessNameGenerator.format.invented") },
    ],
    [t]
  );

  const createName = useCallback(() => {
    const lexicon = INDUSTRY_LEXICON[industry] ?? INDUSTRY_LEXICON.general;
    const adjectives = TONE_ADJECTIVES[tone];
    const keyword = includeKeyword && keywords.length > 0 ? pick(keywords) : undefined;
    const nounSource = [...lexicon.nouns, ...lexicon.prefixes];

    const pickNoun = () => {
      if (keyword && getRandomInt(100) < 55) return keyword;
      return pick(nounSource);
    };

    const pickAdjective = (target?: string) => {
      if (preferAlliteration && target) {
        const targetLetter = target.charAt(0).toLowerCase();
        const matches = adjectives.filter(
          (adj) => adj.charAt(0).toLowerCase() === targetLetter
        );
        if (matches.length > 0) return pick(matches);
      }
      return pick(adjectives);
    };

    if (format === "invented") {
      return buildInventedName(keyword);
    }

    if (format === "suffix") {
      const base = keyword ?? pickAdjective();
      const suffixes = useSuffix ? lexicon.suffixes : ["Co", "Studio", "Works"];
      return `${base} ${pick(suffixes)}`;
    }

    if (format === "one-word") {
      const base = keyword ?? pickAdjective();
      const tail = (keyword ? pick(nounSource) : pickNoun()).replace(/\s+/g, "");
      const combined = `${base}${tail}`.replace(/\s+/g, "");
      return combined;
    }

    const useKeywordAsAdjective = Boolean(keyword) && getRandomInt(100) < 35;
    const noun = useKeywordAsAdjective ? pick(nounSource) : pickNoun();
    const adjective = useKeywordAsAdjective ? keyword! : pickAdjective(noun);
    const separator = format === "hyphenated" ? "-" : " ";
    return `${adjective}${separator}${noun}`;
  }, [industry, tone, format, includeKeyword, keywords, preferAlliteration, useSuffix]);

  const handleGenerate = useCallback(() => {
    const next = new Set<string>();
    const target = Math.max(1, Math.min(50, count));
    let attempts = 0;

    while (next.size < target && attempts < target * 10) {
      const name = createName();
      next.add(toTitleCase(name));
      attempts += 1;
    }

    setNames(Array.from(next));
  }, [count, createName]);

  const handleClear = useCallback(() => {
    setNames([]);
  }, []);

  const handleCopy = useCallback(async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setToastMessage(t("businessNameGenerator.copied"));
    } catch {
      setToastMessage(t("businessNameGenerator.copyFailed"));
    }
  }, [t]);

  const handleCopyAll = useCallback(async () => {
    if (names.length === 0) return;
    try {
      await navigator.clipboard.writeText(names.join("\n"));
      setToastMessage(t("businessNameGenerator.copied"));
    } catch {
      setToastMessage(t("businessNameGenerator.copyFailed"));
    }
  }, [names, t]);

  return (
    <div className="business-name-generator">
      <div className="bng-controls">
        <div className="bng-form">
          <div className="bng-field">
            <label className="bng-label" htmlFor="bng-keywords">
              {t("businessNameGenerator.keywords")}
            </label>
            <input
              id="bng-keywords"
              className="bng-input"
              value={keywordsInput}
              onChange={(e) => setKeywordsInput(e.target.value)}
              placeholder={t("businessNameGenerator.keywordsPlaceholder")}
            />
          </div>

          <div className="bng-field">
            <label className="bng-label" htmlFor="bng-industry">
              {t("businessNameGenerator.industryLabel")}
            </label>
            <select
              id="bng-industry"
              className="bng-select"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
            >
              {industryOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="bng-field">
            <label className="bng-label" htmlFor="bng-tone">
              {t("businessNameGenerator.toneLabel")}
            </label>
            <select
              id="bng-tone"
              className="bng-select"
              value={tone}
              onChange={(e) => setTone(e.target.value as Tone)}
            >
              {toneOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="bng-field">
            <label className="bng-label" htmlFor="bng-format">
              {t("businessNameGenerator.formatLabel")}
            </label>
            <select
              id="bng-format"
              className="bng-select"
              value={format}
              onChange={(e) => setFormat(e.target.value as Format)}
            >
              {formatOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="bng-field">
            <label className="bng-label" htmlFor="bng-count">
              {t("businessNameGenerator.countLabel")}
            </label>
            <input
              id="bng-count"
              type="number"
              className="bng-input"
              min={1}
              max={50}
              value={count}
              onChange={(e) => setCount(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
            />
          </div>
        </div>

        <div className="bng-options">
          <label className="bng-checkbox">
            <input
              type="checkbox"
              checked={includeKeyword}
              onChange={(e) => setIncludeKeyword(e.target.checked)}
            />
            <span>{t("businessNameGenerator.includeKeyword")}</span>
          </label>
          <label className="bng-checkbox">
            <input
              type="checkbox"
              checked={preferAlliteration}
              onChange={(e) => setPreferAlliteration(e.target.checked)}
            />
            <span>{t("businessNameGenerator.alliteration")}</span>
          </label>
          <label className="bng-checkbox">
            <input
              type="checkbox"
              checked={useSuffix}
              onChange={(e) => setUseSuffix(e.target.checked)}
            />
            <span>{t("businessNameGenerator.useSuffix")}</span>
          </label>
        </div>

        <div className="bng-actions">
          <button type="button" className="bng-primary-btn" onClick={handleGenerate}>
            <RefreshCw size={18} />
            {t("businessNameGenerator.generate")}
          </button>
          <button
            type="button"
            className="bng-secondary-btn"
            onClick={handleCopyAll}
            disabled={names.length === 0}
          >
            <Copy size={16} />
            {t("businessNameGenerator.copyAll")}
          </button>
          <button
            type="button"
            className="bng-secondary-btn"
            onClick={handleClear}
            disabled={names.length === 0}
          >
            <Trash2 size={16} />
            {t("businessNameGenerator.clear")}
          </button>
        </div>
      </div>

      <div className="bng-results">
        <div className="bng-results-header">
          <span className="bng-results-title">
            {t("businessNameGenerator.results")}
          </span>
          <span className="bng-results-count">
            {t("businessNameGenerator.resultCount", { count: names.length })}
          </span>
        </div>
        {names.length === 0 ? (
          <div className="bng-empty">{t("businessNameGenerator.empty")}</div>
        ) : (
          <ul className="bng-results-list">
            {names.map((name) => (
              <li key={name} className="bng-result-item">
                <span className="bng-result-name">{name}</span>
                <button
                  type="button"
                  className="bng-copy-btn"
                  onClick={() => handleCopy(name)}
                >
                  <Copy size={14} />
                  {t("businessNameGenerator.copy")}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
